import React, { useState, useRef } from 'react';
import { Upload, X, User } from 'lucide-react';
import { Button } from './Button';
import { toast } from 'react-hot-toast';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { getAuth } from 'firebase/auth';
import { compressImage } from '../../utils/imageUtils';

const functions = getFunctions();
const auth = getAuth();

interface ProfilePhotoUploadProps {
  currentPhotoUrl?: string;
  onPhotoUpload: (photoUrl: string) => void;
  onPhotoRemove: () => void;
  className?: string;
  size?: 'sm' | 'md' | 'lg';
}

export const ProfilePhotoUpload: React.FC<ProfilePhotoUploadProps> = ({
  currentPhotoUrl,
  onPhotoUpload,
  onPhotoRemove,
  className = '',
  size = 'md'
}) => {
  const [isUploading, setIsUploading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [uploadedUrl, setUploadedUrl] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Log para depuración
  console.log('🔍 ProfilePhotoUpload - currentPhotoUrl:', currentPhotoUrl);
  console.log('🔍 ProfilePhotoUpload - previewUrl:', previewUrl);
  console.log('🔍 ProfilePhotoUpload - uploadedUrl:', uploadedUrl);

  const getSizeClasses = () => {
    switch (size) {
      case 'sm':
        return 'w-16 h-16';
      case 'lg':
        return 'w-32 h-32';
      default:
        return 'w-24 h-24';
    }
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validar tipo de archivo
    if (!file.type.startsWith('image/')) {
      toast.error('Por favor selecciona una imagen válida');
      return;
    }

    // Crear preview
    const reader = new FileReader();
    reader.onload = (e) => {
      setPreviewUrl(e.target?.result as string);
    };
    reader.readAsDataURL(file);

    // Subir archivo usando Cloud Function
    uploadPhoto(file);
  };

  const uploadPhoto = async (file: File) => {
    setIsUploading(true);
    try {
      console.log('🔄 Iniciando subida usando Cloud Function...');
      console.log('📁 Archivo original:', { name: file.name, size: file.size, type: file.type });

      // Comprimir imagen
      const compressedFile = await compressImage(file);
      console.log('cy Archivo comprimido:', { name: compressedFile.name, size: compressedFile.size, type: compressedFile.type });

      // Verificar que el usuario esté autenticado
      const currentUser = auth.currentUser;
      if (!currentUser) {
        toast.error('Debes estar autenticado para subir fotos');
        return;
      }

      console.log('👤 Usuario autenticado:', currentUser.uid);

      // Obtener el token de autenticación
      const authToken = await currentUser.getIdToken();
      console.log('🔍 Token de autenticación:', authToken);

      // Convertir archivo (comprimido) a base64
      const base64Content = await fileToBase64(compressedFile);

      // Usar fetch para llamar a la función Cloud Run
      const response = await fetch('https://uploadprofilephoto-bahfx4ta4q-uc.a.run.app', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`
        },
        body: JSON.stringify({
          userId: currentUser.uid,
          fileContent: base64Content,
          contentType: file.type,
          fileName: file.name
        })
      });

      if (!response.ok) {
        throw new Error('Error al subir la foto');
      }

      const data = await response.json();
      console.log('✅ Foto subida exitosamente:', data);
      onPhotoUpload(data.downloadURL);
    } catch (error) {
      console.error('❌ Error con Cloud Function:', error);
      toast.error('Error al subir la foto');
    } finally {
      setIsUploading(false);
    }
  };

  // Función auxiliar para convertir archivo a base64
  const fileToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        const result = reader.result as string;
        // Remover el prefijo "data:image/jpeg;base64," para obtener solo el base64
        const base64 = result.split(',')[1];
        resolve(base64);
      };
      reader.onerror = error => reject(error);
    });
  };

  const handleRemovePhoto = () => {
    setPreviewUrl(null);
    setUploadedUrl(null);
    onPhotoRemove();
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const displayUrl = previewUrl || uploadedUrl || currentPhotoUrl;

  return (
    <div className={`flex flex-col items-center space-y-4 ${className}`}>
      <div className="relative">
        <div className={`${getSizeClasses()} rounded-full overflow-hidden bg-gray-200 flex items-center justify-center`}>
          {displayUrl ? (
            <img
              src={displayUrl}
              alt="Foto de perfil"
              className="w-full h-full object-cover"
            />
          ) : (
            <User className="w-1/2 h-1/2 text-gray-400" />
          )}
        </div>

        {displayUrl && (
          <button
            onClick={handleRemovePhoto}
            className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600 transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        )}
      </div>

      <div className="flex flex-col space-y-2">
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          onChange={handleFileSelect}
          className="hidden"
          disabled={isUploading}
        />

        <Button
          onClick={() => fileInputRef.current?.click()}
          disabled={isUploading}
          className="flex items-center space-x-2"
        >
          <Upload className="w-4 h-4" />
          <span>{isUploading ? 'Subiendo...' : 'Cambiar Foto'}</span>
        </Button>
      </div>
    </div>
  );
}; 